import React, { useRef, useState } from 'react';
import cn from 'classnames';
import { AudioBlockData } from '../../../../models/courses';
import { resolveMediaUrl } from './mediaUrl';
import styles from './AudioBlock.module.css';

interface AudioBlockProps {
  data: AudioBlockData;
  onChange: (_data: AudioBlockData) => void;
  onUpload: (_file: File) => Promise<string>;
  disabled?: boolean;
}

const UploadIcon = () => (
  <svg width="28" height="28" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path
      d="M12 16V4m0 0L8 8m4-4l4 4"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M4 15v3a2 2 0 002 2h12a2 2 0 002-2v-3"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const NoteIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M12 3l-1 1v11.1A4 4 0 1013 18V8h4V4h-5V3zm-1 15a2 2 0 110-4 2 2 0 010 4z" />
  </svg>
);

const ReplaceIcon = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M12 5V1L7 6l5 5V7a5 5 0 11-5 5H5a7 7 0 107-7z" />
  </svg>
);

export const AudioBlock: React.FC<AudioBlockProps> = ({
  data,
  onChange,
  onUpload,
  disabled,
}) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);

  const openPicker = () => {
    if (!disabled && !uploading) {
      inputRef.current?.click();
    }
  };

  const upload = async (file: File) => {
    setUploading(true);
    try {
      const url = await onUpload(file);
      onChange({ ...data, src: url });
    } finally {
      setUploading(false);
      if (inputRef.current) {
        inputRef.current.value = '';
      }
    }
  };

  const handleFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      upload(file);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    if (disabled || uploading) {
      return;
    }
    const file = e.dataTransfer.files?.[0];
    if (file && file.type.startsWith('audio/')) {
      upload(file);
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    if (!disabled && !uploading) {
      setDragOver(true);
    }
  };

  return (
    <div className={styles.wrap}>
      <input
        ref={inputRef}
        type="file"
        accept="audio/mpeg,audio/mp3,audio/wav,audio/ogg,audio/x-m4a,.mp3,.wav,.ogg,.m4a"
        style={{ display: 'none' }}
        onChange={handleFile}
        disabled={disabled || uploading}
      />

      {data.src ? (
        <div className={styles.player}>
          <span className={styles.playerIcon}>
            {uploading ? <span className={styles.spinner} /> : <NoteIcon />}
          </span>
          <audio
            className={styles.audio}
            src={resolveMediaUrl(data.src)}
            controls
            preload="metadata"
          />
          <button
            type="button"
            className={styles.replaceBtn}
            onClick={openPicker}
            disabled={disabled}
          >
            <ReplaceIcon />
            Заменить
          </button>
        </div>
      ) : (
        <div
          className={cn(styles.dropzone, { [styles.dropzoneOver]: dragOver })}
          onClick={openPicker}
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          onDragLeave={() => setDragOver(false)}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              openPicker();
            }
          }}
        >
          {uploading ? (
            <span className={styles.spinner} />
          ) : (
            <>
              <span className={styles.dropIcon}>
                <UploadIcon />
              </span>
              <span className={styles.dropTitle}>Загрузить аудио</span>
              <span className={styles.dropHint}>
                mp3, wav, ogg, m4a — нажмите или перетащите файл
              </span>
            </>
          )}
        </div>
      )}
    </div>
  );
};
